// React
import React, { useState, useEffect, useRef, useContext } from 'react';

// Third party
import { trackPromise } from 'react-promise-tracker';

// Styles
import styles from './Table.module.scss';

// Material UI
import { LinearProgress } from '@material-ui/core';
import { DataGrid, GridOverlay } from '@material-ui/data-grid';

// Contexts
import { ListingContext } from '../../../contexts/listing';

// Services
import { getListingData as getDataTypeData } from '../../../services/data-types';
import { getListingData as getTaxonomyData } from '../../../services/taxonomies';
import { getListingData as getSalesChannelData } from '../../../services/sales-channel';

// Components
import ColumnsSelector from '../ColumnsSelector/ColumnsSelector';

// Hooks
import useMounted from '../../../hooks/useMounted';

// No rows component
function NoRowsOverlay() {
  return (
    <GridOverlay className={styles['no-data']}>
      <div>
        <strong>No matching records found</strong>
      </div>
      <div>Try adjusting your search by changing or removing filters</div>
    </GridOverlay>
  );
}

// This component
export default function Table() {

  // Listing Context
  const {
    typeOfData,
    endpoint,
    getColumns,
    getRows,
    activeFilters,
    page,
    pageSize,
    setPage,
    setPageSize,
    sortModel,
    setSortModel,
    refreshDataGrid,
    setRefreshDataGrid
  } = useContext(ListingContext);

  // If the component is mounted
  const mounted = useMounted();

  // Data
  const [data, setData] = useState({
    totalRecords: 0,
    data: []
  });

  // Columns
  const [columns, setColumns] = useState([]);

  // Keep reference to the inital passed function
  // (we don't need to watch its changes, just need to invoke)
  const getRowsRef = useRef(getRows);
  const getColumnsRef = useRef(getColumns);

  // Page change
  const handlePageChange = (params) => {
    setPage(params.page);
    setRefreshDataGrid(true);
  };

  // Page size change
  const handlePageSizeChange = (params) => {
    setPageSize(params.pageSize);
    setRefreshDataGrid(true);
  };

  // Sort change
  const handleSortChange = (params) => {
    if (params.sortModel !== sortModel) {
      setSortModel(params.sortModel);
      setRefreshDataGrid(true);
    }
  };

  // Get data
  useEffect(() => {
    if (refreshDataGrid) {
      let promise = null;

      if (typeOfData === 'taxonomy') {
        let dependOnValue = [];

        if (activeFilters) {
          for (const [key, value] of Object.entries(activeFilters)) {
            dependOnValue.push({
              filter: key,
              value: value
            });
          }
        }

        promise = getTaxonomyData({
          endpoint,
          getRows: getRowsRef.current,
          dependOnValue: dependOnValue
        });
      } else if (typeOfData === 'sales-channel') {
        promise = getSalesChannelData({
          endpoint,
          pageSize,
          getRows: getRowsRef.current,
          page,
          sortModel,
          activeFilters
        });
      } else {
        promise = getDataTypeData({
          endpoint,
          pageSize,
          getRows: getRowsRef.current,
          page,
          sortModel,
          activeFilters
        });
      }

      trackPromise(
        promise.then((newData) => {
          if (
            mounted.current &&
            newData &&
            newData.data &&
            typeof newData.data === 'object'
          ) {
            setData(newData);
            setColumns((columns) =>
              getColumnsRef.current(newData.data, columns)
            );
            setRefreshDataGrid(false);
          }
        })
      );
    }
  }, [
    endpoint,
    pageSize,
    page,
    sortModel,
    activeFilters,
    refreshDataGrid,
    setRefreshDataGrid,
    typeOfData,
    mounted
  ]);

  // JSX
  return (
    <>
      <ColumnsSelector columns={columns} setColumns={setColumns} />
      {refreshDataGrid && <LinearProgress />}
      {!refreshDataGrid && (
        <DataGrid
          columns={columns}
          rows={data.data}
          autoHeight={true}
          rowHeight={40}
          disableSelectionOnClick={true}
          disableColumnMenu={true}
          pageSize={pageSize ? pageSize : data.data.length}
          rowCount={data.totalRecords || data.data.length}
          paginationMode={typeOfData === 'taxonomy' ? 'client' : 'server'}
          page={page !== null ? page : 0}
          onPageChange={page !== null ? handlePageChange : null}
          onPageSizeChange={page !== null ? handlePageSizeChange : null}
          rowsPerPageOptions={[25, 50, 100]}
          sortingOrder={['desc', 'asc']}
          sortingMode={typeOfData === 'taxonomy' ? 'client' : 'server'}
          sortModel={
            sortModel &&
            sortModel[0] &&
            typeof sortModel[0].field !== 'undefined'
              ? sortModel
              : null
          }
          onSortModelChange={
            typeOfData === 'taxonomy' ? null : handleSortChange
          }
          components={{
            NoRowsOverlay: NoRowsOverlay
          }}
        />
      )}
    </>
  );
}
